import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L, { LatLngTuple } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TextField, Button, Container, Typography, Box, List, ListItemText, ListItemButton, useTheme } from '@mui/material';
import { useOutletContext, useNavigate } from 'react-router-dom';
import PropertyFooterLayout from '../../../components/properties/PropertyFooterLayout';
import { getCostaRicaDistrictDetails } from "../../../utils/map"
import{searchGeo, reverseGeo } from "../../../services/geolocationService";

type PublishOutletContext = {
  handleNavigateToConfirmLocation: () => void;
  addressDetails: {
    country: string;
    province: string;
    county: string;
    district: string;
    streetAddress: string;
    postalCode: string;
    nearbyLandmark: string;
    googleMapsUrl: string;
    latitude: number;
    longitude: number;
    Zone: string;
    neighborhood?: string
  };
  setAddressDetails: (details: {
    country: string;
    province: string;
    county: string;
    district: string;
    streetAddress: string;
    postalCode: string;
    nearbyLandmark: string;
    googleMapsUrl: string;
    latitude: number;
    longitude: number;
    Zone: string;
    neighborhood?: string
  }) => void;
};

const progress = 50;
const step = 1;

const defaultCoordinates: LatLngTuple = [9.9286, -84.0907]; // San José, Costa Rica centro

export const calcZone = (latitude: number, longitude: number): string => {
  // Coordenadas aproximadas del GAM (Gran Área Metropolitana)
  const GAM_BOUNDS = {
    north: 10.1,
    south: 9.7,
    west: -84.35,
    east: -83.9,
  };

  //Determinar si está dentro o fuera del GAM
  const isInGAM =
    latitude >= GAM_BOUNDS.south &&
    latitude <= GAM_BOUNDS.north &&
    longitude >= GAM_BOUNDS.west &&
    longitude <= GAM_BOUNDS.east;

  //Determinar zona cardinal dentro del GAM
  if (isInGAM) {
    if (latitude > 9.95 && longitude < -84.1) return 'Noroeste (GAM)';
    if (latitude > 9.95 && longitude >= -84.1) return 'Noreste (GAM)';
    if (latitude <= 9.95 && longitude < -84.1) return 'Suroeste (GAM)';
    if (latitude <= 9.95 && longitude >= -84.1) return 'Sureste (GAM)';
    return 'GAM';
  }

  // Fuera del GAM - aproximación por latitud y longitud
  if (latitude > GAM_BOUNDS.north) return 'Norte (Fuera del GAM)';
  if (latitude < GAM_BOUNDS.south) return 'Sur (Fuera del GAM)';
  if (longitude < GAM_BOUNDS.west) return 'Oeste (Fuera del GAM)';
  if (longitude > GAM_BOUNDS.east) return 'Este (Fuera del GAM)';

  return 'Zona Desconocida';
};



export const MapComponent: React.FC<{ position: LatLngTuple; onMarkerDragEnd: (latlng: LatLngTuple) => void }> = ({ position, onMarkerDragEnd }) => {
  const map = useMap();
  map.setView(position);

  return (
    <Marker
      position={position}
      draggable={true}
      eventHandlers={{
        dragend: (event) => {
          const latlng = event.target.getLatLng();
          onMarkerDragEnd([latlng.lat, latlng.lng]);
        },
      }}
      icon={L.icon({
        iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
        shadowSize: [41, 41],
      })}
    >
      <Popup>
        <span>Ubicación seleccionada</span>
      </Popup>
    </Marker>
  );
};

const PropertyMapPage: React.FC = () => {
  const [selectedLocation, setSelectedLocation] = useState<LatLngTuple>(defaultCoordinates);
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const { handleNavigateToConfirmLocation, addressDetails, setAddressDetails } = useOutletContext<PublishOutletContext>();
  const navigate = useNavigate();
  const theme = useTheme();

  const isLocationSelected = !!(addressDetails.latitude && addressDetails.longitude);

  useEffect(() => {
    if (addressDetails.latitude && addressDetails.longitude) {
      setSelectedLocation([addressDetails.latitude, addressDetails.longitude]);
    } else {
      setSelectedLocation(defaultCoordinates);
    }
  }, [addressDetails]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleContinue = () => {
    if (isLocationSelected) {
      handleNavigateToConfirmLocation();
    }
  };

  const handleUseCurrentLocation = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const newLocation: LatLngTuple = [
            position.coords.latitude,
            position.coords.longitude,
          ];
          setSelectedLocation(newLocation);

          try {
            const data = await reverseGeo(position.coords.latitude, position.coords.longitude);

            const address = data.address || {};
            const district =  address.village || address.suburb || address.district || address.neighbourhood || ""
            setAddressDetails({
              country: address.country || '',
              province: address.state || address.province || '',
              county: address.county || '',
              district: district,
              streetAddress: address.road || '',
              postalCode: address.postcode || '',
              nearbyLandmark: '',
              googleMapsUrl: `https://www.google.com/maps?q=${position.coords.latitude},${position.coords.longitude}`,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              Zone: address.Zone || '',
            });
          } catch (error) {
            console.error('Error al obtener la dirección de la ubicación actual:', error);
            alert('No se pudo obtener la dirección de la ubicación actual.');
          }
        },
        (error) => {
          console.error('Error al obtener la ubicación actual:', error);
          alert('No se pudo obtener la ubicación actual.');
        },
        { enableHighAccuracy: true }
      );
    } else {
      alert('La geolocalización no está soportada por este navegador.');
    }
  };

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setShowSuggestions(true);

    if (value.length > 2) {
      try {
      const results = await searchGeo(value);

        setSuggestions(results);
      } catch (error) {
        console.error('Error al obtener las sugerencias:', error);
      }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: any) => {
    const newLocation: LatLngTuple = [parseFloat(suggestion.lat), parseFloat(suggestion.lon)];
    setSelectedLocation(newLocation);
    setSearchTerm(suggestion.display_name);
    setAddressDetails({
      country: suggestion.address.country || '',
      province: suggestion.address.state || suggestion.address.province || '',
      county: suggestion.address.county || '',
      district: suggestion.address.village || suggestion.address.suburb || suggestion.address.district || suggestion.address.neighbourhood || "",
      streetAddress: suggestion.address.road || '',
      postalCode: suggestion.address.postcode || '',
      nearbyLandmark: '',
      googleMapsUrl: `https://www.google.com/maps?q=${suggestion.lat},${suggestion.lon}`,
      latitude: suggestion.lat,
      longitude: suggestion.lon,
      Zone: suggestion.Zone || '',
    });
    setSuggestions([]);
    setShowSuggestions(false);
  };


  const handleMarkerDragEnd = async (latlng: LatLngTuple) => {
    console.log("New LatLng:", latlng)
    setSelectedLocation(latlng)

    try {
      const districtDetails = await getCostaRicaDistrictDetails(latlng[0], latlng[1])

      setAddressDetails({
        country: districtDetails.country || "Costa Rica",
        province: districtDetails.province || "",
        county: districtDetails.county || "",
        district: districtDetails.district || "",
        neighborhood: districtDetails.neighborhood || "",
        streetAddress: districtDetails.road || "",
        postalCode: districtDetails.postcode || "",
        nearbyLandmark: "",
        googleMapsUrl: `https://www.google.com/maps?q=${latlng[0]},${latlng[1]}`,
        latitude: latlng[0],
        longitude: latlng[1],
        Zone: calcZone(latlng[0], latlng[1]),
      })
    } catch (error) {
      console.error("Error al obtener la dirección:", error)
      alert("No se pudo obtener la dirección de la nueva ubicación.")
    }
  }

  return (
    <Container maxWidth="md" sx={{pb:12}}>
      <>
        <Typography variant="h4" gutterBottom>
          Selecciona la dirección de tu propiedad
        </Typography>
        <Box mb={3}>
          <TextField
            label="Ingresa tu dirección"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={handleInputChange}
            InputProps={{
              sx: {
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
            InputLabelProps={{
              sx: {
                '&.Mui-focused': {
                  color: theme.palette.primary.main, 
                },
              },
            }}
          />

          <Box mt={2}>
            <Button variant="contained" color='secondary' onClick={handleUseCurrentLocation}>
              Usar Ubicación Actual
            </Button>
          </Box>
          {showSuggestions && suggestions.length > 0 && (
            <Box mt={2} ref={suggestionsRef} sx={{ border: '1px solid #ccc', borderRadius: 1, maxHeight: '200px', overflowY: 'auto' }}>
              <List>
                {suggestions.map((suggestion: any) => (
                  <ListItemButton key={suggestion.place_id} onClick={() => handleSuggestionClick(suggestion)}>
                    <ListItemText primary={suggestion.display_name} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          )}
        </Box>
        <MapContainer center={selectedLocation} zoom={13} style={{ height: '400px', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <MapComponent position={selectedLocation} onMarkerDragEnd={handleMarkerDragEnd} />
        </MapContainer>
      </>
      <PropertyFooterLayout 
        progress={progress} 
        step={step} 
        onBack={handleBack} 
        onContinue={handleContinue} 
        isContinueDisabled={!isLocationSelected} 
      />
    </Container>
  );
};

export default PropertyMapPage;

